import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { SingleSelectComboboxField, SelectOption } from '@/components/form-components/SingleSelectCombobox'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { Badge } from '@/components/shadcn/badge'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { getInitials } from '@/lib/utils'
import { useSessionStorage } from '@/hooks'
import { isPlatformAdmin, isProjectAdmin, isProjectLeader, isSuperUser } from '@/util/roles'
import { ProjectAccessRequestSection } from './components/ProjectAccessRequestSection'

const formSchema = z.object({
  organisationId: z.number().min(1, 'Please select an organisation'),
  userId: z.number().min(1, 'Please select a user to add'),
})

export type MaintainProjectMembersFormValues = z.infer<typeof formSchema>

type MessagesProjectsOrganisationProject = RestSchemas['Messages.Projects.OrganisationProject']
type MessagesProjectsOrganisationProjectUser = RestSchemas['Messages.Projects.OrganisationProjectUser']
type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']

type MaintainProjectMembersFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  organisationId?: number
  selectedProject: MessagesProjectsOrganisationProject
  members: MessagesProjectsOrganisationProjectUser[]
}

export const MaintainProjectMembersForm = (props: MaintainProjectMembersFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: MaintainProjectMembersFormProps) => {
  const projectOrgId = props.organisationId

  if (projectOrgId == null || !Number.isFinite(projectOrgId)) {
    return <ErrorComponent />
  }

  return <FormComponentWithProjectOrg {...props} projectOrgId={projectOrgId} />
}

const FormComponentWithProjectOrg = (
  props: MaintainProjectMembersFormProps & { projectOrgId: number }
) => {
  const { projectOrgId } = props
  const [userRole] = useSessionStorage('roles', 1)

  const canDirectAdd =
    isSuperUser(userRole) || isPlatformAdmin(userRole) || isProjectLeader(userRole) || isProjectAdmin(userRole)

  const {
    getSubscriptionsApiGetOrganisations,
    getSubscriptionsApiListOrganisationUsers,
    putProjectsApiAddProjectMember,
    deleteProjectsApiRemoveProjectMember,
    getProjectsApiListOrganisationProjects,
    getProjectsApiGetUserLinkedProjects,
  } = useRestClient()

  const invalidateQueries = () => {
    getProjectsApiListOrganisationProjects.invalidateQueries({ parameters: { query: { orgId: projectOrgId } } })
    getProjectsApiGetUserLinkedProjects.invalidateQueries({})
  }

  const {
    data: organisations,
    isPending: organisationsLoading,
    isError: organisationsError,
  } = getSubscriptionsApiGetOrganisations.useQuery(undefined)

  const organisationOptions: SelectOption<number>[] = useMemo(
    () => organisations?.map((org) => ({ value: org.id!, label: org.name ?? `Organisation ${org.id}` })) ?? [],
    [organisations]
  )

  const defaultValues = useMemo(
    () => ({
      organisationId: projectOrgId,
      userId: 0,
    }),
    [projectOrgId]
  )

  const form = useForm<MaintainProjectMembersFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const selectedOrganisationId = form.watch('organisationId')

  useEffect(() => {
    form.setValue('userId', 0)
  }, [form, selectedOrganisationId])

  const {
    data: orgUsers,
    isPending: orgUsersLoading,
    isError: orgUsersError,
  } = getSubscriptionsApiListOrganisationUsers.useQuery(
    { query: { orgId: selectedOrganisationId } },
    {
      select: (data) => data.filter((user) => user.active),
      enabled: selectedOrganisationId > 0,
    }
  )

  const { mutateAsync: addProjectMember, isPending: isAdding } = putProjectsApiAddProjectMember.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      toast.success('Member successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding member')
    },
  })

  const { mutate: deleteProjectMember } = deleteProjectsApiRemoveProjectMember.useMutation(undefined, {
    onSuccess: () => {
      invalidateQueries()
      toast.success('Member successfully removed')
    },
    onError: () => {
      toast.error('Error removing member')
    },
  })

  const getUserDisplayName = (user: MessagesSubscriptionsOrganisationUser): string => {
    return user.givenName || user.familyName
      ? `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim()
      : 'Unknown User'
  }

  const memberOptions: SelectOption<number>[] = useMemo(() => {
    if (!orgUsers) return []

    const sortedUsers = [...orgUsers].sort((a, b) => (a.familyName ?? '').localeCompare(b.familyName ?? ''))

    const nameCounts: Record<string, number> = {}
    sortedUsers.forEach((user) => {
      const displayName = getUserDisplayName(user)
      nameCounts[displayName] = (nameCounts[displayName] || 0) + 1
    })

    return sortedUsers
      .filter((user) => !props.members.some((member) => member.userId === user.id))
      .map((user) => {
        const baseName = getUserDisplayName(user)
        const label = nameCounts[baseName] > 1 ? `${baseName} (ID: ${user.id})` : baseName
        return { value: user.id!, label }
      })
  }, [orgUsers, props.members])

  const handleSubmit = async (data: MaintainProjectMembersFormValues) => {
    if (data.organisationId !== projectOrgId) {
      toast.error('Members must belong to the same organisation as the project')
      return
    }

    await addProjectMember({
      query: { userId: data.userId, projectId: props.selectedProject.projectId },
    })
    form.setValue('userId', 0)
  }

  const handleDeleteMember = (userId: number) => {
    deleteProjectMember({
      query: { userId, projectId: props.selectedProject.projectId },
    })
  }

  if (organisationsLoading) return <FormComponentLoading />
  if (organisationsError) return <ErrorComponent />

  const usersLoading = selectedOrganisationId > 0 && orgUsersLoading
  if (usersLoading) return <FormComponentLoading />
  if (orgUsersError) return <ErrorComponent />

  return (
    <>
      {canDirectAdd ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="flex h-full flex-col">
              <p className="mb-4 text-sm text-muted-foreground">
                Project members can access Databricks notebooks and catalogue workspaces linked to this project.
              </p>
              <div className="space-y-6">
                <SingleSelectComboboxField<number, MaintainProjectMembersFormValues>
                  control={form.control}
                  name="organisationId"
                  label="Organisation"
                  options={organisationOptions}
                  placeholder="Select an organisation..."
                  searchPlaceholder="Search organisations..."
                  emptyMessage="No organisations found."
                />
                <SingleSelectComboboxField<number, MaintainProjectMembersFormValues>
                  control={form.control}
                  name="userId"
                  label="User"
                  options={memberOptions}
                  placeholder="Select a user to add..."
                  searchPlaceholder="Search organisation users..."
                  emptyMessage="No available users found."
                  allowClear
                  disabled={selectedOrganisationId <= 0}
                />
              </div>
              <div className="form-action-container">
                <Button type="submit" disabled={isAdding || memberOptions.length === 0}>
                  Add
                </Button>
              </div>
            </div>
          </form>
        </Form>
      ) : (
        <p className="mb-4 text-sm text-muted-foreground">
          You can request workspace access for this project. Project leaders or administrators can add members directly.
        </p>
      )}

      {props.members.length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-medium">Current project members</div>
          <div className="space-y-2">
            {props.members.map((member) => (
              <ProjectMemberCard
                key={member.userId}
                member={member}
                onDelete={canDirectAdd ? () => handleDeleteMember(member.userId) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      <ProjectAccessRequestSection
        projectId={props.selectedProject.projectId!}
        organisationId={projectOrgId}
      />
    </>
  )
}

const ProjectMemberCard = ({
  member,
  onDelete,
}: {
  member: MessagesProjectsOrganisationProjectUser
  onDelete?: () => void
}) => {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-background p-3">
      <div className="flex items-center gap-5">
        <Avatar className="ring-2 ring-background">
          <AvatarFallback className="bg-accent text-sm text-accent-foreground">
            {getInitials(member.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{member.name}</span>
          <Badge variant="outline">{member.userId}</Badge>
        </div>
      </div>
      {onDelete && (
        <Button variant="ghost" size="icon" onClick={onDelete}>
          <IconDeleteOutline />
        </Button>
      )}
    </div>
  )
}
